import { Component, input, output, signal, ViewChild } from '@angular/core';
import { MemberListPage } from "../../../member/components/member-list/member-list.component";
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { PropertyMemberTypes } from '../../../../core/enums/data.enums';
import { MemberListViewLocations } from '../../../../core/enums/base.enums';
import { PropertiesService } from '../../../../core/services/property.service';
import { InfoModalComponent } from "../../../../parts/info-modal/info-modal.component";
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PropertiesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';

@Component({
  selector: 'ms-add-members-to-property',
  imports: [MemberListPage, InfoModalComponent, ConfirmationModalComponent, FontAwesomeModule, EscCloseDirective],
  templateUrl: './add-members-to-property.component.html'
})
export class AddMembersToPropertyComponent {
  @ViewChild(MemberListPage) memberListRef!: MemberListPage;

  public propertyId = input<number>();
  public memberListViewLocations = MemberListViewLocations;
  public PropertyMemberTypes = PropertyMemberTypes;
  
  // State signals
  public propertyMemberTypeToAdd = signal<PropertyMemberTypes>(PropertyMemberTypes.Owner);
  public memberIdsToAdd = signal<number[]>([]);
  
  public isSubmitting = false;
  private lastAddSucceeded = false;

  // Output event to notify parent when members are added to property
  public membersAddedToProperty = output<void>();

  @ViewChild('addMembersToPropertyConfirmationModalRef') addMembersToPropertyConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('addMembersToPropertyInfoModalRef') addMembersToPropertyInfoModalRef!: InfoModalComponent;

  constructor(
    private bootstrapService: BootstrapService,
    private propertiesService: PropertiesService
  ) { }

  public onAddMembersRequested(event: {memberIds: number[], memberType: PropertyMemberTypes}): void {
    this.memberIdsToAdd.set(event.memberIds);
    this.propertyMemberTypeToAdd.set(event.memberType);

    this.addMembersToPropertyConfirmationModalRef.modalType.set(ModalTypes.Question);
    
    this.addMembersToPropertyConfirmationModalRef.message.set(PropertiesMessagingConstants.PROPERTIES_ADD_MEMBERS_TO_PROPERTY_CONFIRMATION());

    this.bootstrapService.manageModal("#addMembersToPropertyModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addMembersToPropertyConfirmationModal", ModalActions.Show);
  }

  public async addMembersToProperty(): Promise<void> {
    if (this.propertyId()) {
      // Get selected member IDs from the member list component
      const selectedMemberIds = this.getSelectedMemberIds();
      
      if (selectedMemberIds.length === 0) {
        this.lastAddSucceeded = false;
        this.addMembersToPropertyInfoModalRef.modalType.set(ModalTypes.Failure);
        this.addMembersToPropertyInfoModalRef.message.set(PropertiesMessagingConstants.PROPERTIES_ERROR_NO_MEMBER_SELECTED());

        this.bootstrapService.manageModal("#addMembersToPropertyConfirmationModal", ModalActions.Hide);
        this.bootstrapService.manageModal("#addMembersToPropertyInfoModal", ModalActions.Show);
        return;
      }

      this.isSubmitting = true;
      try {
        const result = await this.propertiesService.addMembersToProperty(
          this.propertyId()!,
          {memberIds: selectedMemberIds,
          memberType: this.propertyMemberTypeToAdd()}
        );

        this.lastAddSucceeded = result.success === true;
        this.addMembersToPropertyInfoModalRef.modalType.set(result.success ? ModalTypes.Success : ModalTypes.Failure);
        this.addMembersToPropertyInfoModalRef.message.set(result.message!);

        this.bootstrapService.manageModal("#addMembersToPropertyConfirmationModal", ModalActions.Hide);
        this.bootstrapService.manageModal("#addMembersToPropertyInfoModal", ModalActions.Show);
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  private getSelectedMemberIds(): number[] {
    if (!this.memberListRef) {
      return [];
    }

    const memberList = this.memberListRef.memberList();
    const selectionItems = this.memberListRef.selectionItems();
    const isSelectAll = this.memberListRef.isSelectAll;

    if (isSelectAll) {
      // When selectAll is true, selectionItems contains deselected items
      // So we return all members except those in selectionItems
      return memberList
        .filter(member => !selectionItems.includes(member.memberId))
        .map(member => member.memberId);
    } else {
      // When selectAll is false, selectionItems contains selected items
      return selectionItems;
    }
  }

  public abortAddMembersToPropertyConfirmation(): void {
    this.bootstrapService.manageModal("#addMembersToPropertyConfirmationModal", ModalActions.Hide);
    this.bootstrapService.manageModal("#addMembersToPropertyModal", ModalActions.Show);
  }
  
  public onCancelRequested(): void {
    this.bootstrapService.manageModal("#addMembersToPropertyModal", ModalActions.Hide);
  }
  
  public openAddMembersToPropertyModal(): void {
    this.bootstrapService.manageModal("#addMembersToPropertyModal", ModalActions.Show);
  }

  public onConfirmInfoModal(): void {
    if (this.lastAddSucceeded) {
      this.lastAddSucceeded = false;
      this.membersAddedToProperty.emit();
    }
  }
}